
import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Sparkles, User, RefreshCw } from 'lucide-react';
import { Message } from '../types';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// CleverDigital Logo Avatar
const CleverDigitalAvatar = () => (
  <img
    src="/assets/cd-mark-gray.png"
    alt="CleverDigital"
    className="w-full h-full p-1 object-contain"
  />
);

export const NeuralChat: React.FC = () => {
  const { t, i18n } = useTranslation();
  const placeholders = t('shared.neural_chat.placeholders', { returnObjects: true }) as string[];

  const [isExpanded, setIsExpanded] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [streamedText, setStreamedText] = useState('');
  const [messageCount, setMessageCount] = useState(0);
  const MESSAGE_LIMIT = 10;

  const [placeholder, setPlaceholder] = useState('');
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  const [containerHeight, setContainerHeight] = useState(64);
  const historyRef = useRef<HTMLDivElement>(null);

  // Typewriter Effect for Placeholder
  useEffect(() => {
    if (input) return;
    const currentText = placeholders[placeholderIndex];
    if (!currentText) return;

    const typeSpeed = isDeleting ? 30 : 60;
    if (isPaused) {
      const timer = setTimeout(() => { setIsPaused(false); setIsDeleting(true); }, 2000);
      return () => clearTimeout(timer);
    }
    const timer = setTimeout(() => {
      if (!isDeleting) {
        if (charIndex < currentText.length) {
          setPlaceholder(currentText.substring(0, charIndex + 1));
          setCharIndex(prev => prev + 1);
        } else { setIsPaused(true); }
      } else {
        if (charIndex > 0) {
          setPlaceholder(currentText.substring(0, charIndex - 1));
          setCharIndex(prev => prev - 1);
        } else {
          setIsDeleting(false);
          setPlaceholderIndex(prev => (prev + 1) % placeholders.length);
        }
      }
    }, typeSpeed);
    return () => clearTimeout(timer);
  }, [charIndex, isDeleting, isPaused, placeholderIndex, input, placeholders]);

  // Dynamic Height Expansion
  useEffect(() => {
    if (isExpanded && historyRef.current) {
      const contentHeight = historyRef.current.scrollHeight;
      const totalNeeded = contentHeight + 64;
      setContainerHeight(Math.min(totalNeeded, 500));
    }
  }, [messages, isLoading, isExpanded, streamedText]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;
    if (messageCount >= MESSAGE_LIMIT) {
      const limitMessage: Message = {
        id: Date.now().toString(),
        role: 'bot',
        text: i18n.language === 'en'
          ? "You've explored a lot! To discuss your specific project in detail, please book a free strategy session: [www.cleverdigital.at/contact](/contact)"
          : "Sie haben bereits viele Einblicke gewonnen! Um Ihr spezifisches Projekt im Detail zu besprechen, buchen Sie bitte ein kostenloses Strategiegespräch: [www.cleverdigital.at/contact](/contact)",
        timestamp: Date.now()
      };
      setMessages(prev => [...prev, limitMessage]);
      setInput('');
      return;
    }

    setIsExpanded(true);
    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      text: input,
      timestamp: Date.now()
    };

    setMessages(prev => [...prev, userMessage]);
    setMessageCount(prev => prev + 1);
    setInput('');
    setIsLoading(true);

    try {
      // Convert messages to history format
      const history = messages.map(m => ({
        role: m.role === 'user' ? 'user' : 'model',
        parts: [{ text: m.text }],
      }));

      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage.text,
          history: history,
          language: i18n.language
        }),
      });

      const data = await response.json();

      if (!response.ok) throw new Error(data.error || 'Failed to fetch response');

      const botMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'bot', // Keeping 'bot' to match existing type definition if strictly typed, or 'system'
        text: data.text,
        timestamp: Date.now()
      };
      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      console.error('Chat Error:', error);
      const errorMessage: Message = {
        id: Date.now().toString(),
        role: 'bot',
        text: t('shared.neural_chat.error'),
        timestamp: Date.now()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto relative z-40">
      <div
        className="bg-white border border-gray-200 rounded-[2rem] shadow-2xl overflow-hidden flex flex-col transition-all ease-out"
        style={{ height: `${containerHeight}px`, transitionDuration: '600ms' }}
      >
        <div ref={historyRef} className="flex-1 overflow-y-auto bg-gray-50/50 custom-scrollbar">
          <div className="p-6 space-y-6">
            {messages.map((msg) => (
              <div key={msg.id} className={`flex gap-4 ${msg.role === 'user' ? 'flex-row-reverse' : ''} animate-fade-in`}>
                <div className={`w-8 h-8 rounded-full shrink-0 flex items-center justify-center border overflow-hidden ${msg.role === 'user' ? 'bg-white border-gray-200' : 'bg-white border-clever-teal/20'}`}>
                  {msg.role === 'user' ? <User size={14} className="text-gray-400" /> : <CleverDigitalAvatar />}
                </div>
                <div className={`p-4 rounded-2xl text-sm max-w-[80%] shadow-sm ${msg.role === 'user' ? 'bg-[#0B1121] text-white' : 'bg-white border border-gray-100 text-gray-700'}`}>
                  {msg.role === 'user' ? (
                    msg.text
                  ) : (
                    <div className="prose prose-sm max-w-none prose-p:leading-relaxed prose-headings:font-bold prose-headings:text-gray-900 prose-ul:my-2 prose-li:my-0 prose-strong:text-clever-teal prose-a:text-clever-teal prose-a:underline hover:prose-a:text-black transition-colors">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" />
                        }}
                      >
                        {msg.text}
                      </ReactMarkdown>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="flex gap-4 animate-fade-in">
                <div className="w-8 h-8 rounded-full bg-white border border-clever-teal/20 shrink-0 flex items-center justify-center overflow-hidden">
                  <CleverDigitalAvatar />
                </div>
                <div className="bg-white border border-gray-100 p-4 rounded-2xl shadow-sm text-sm text-gray-700 max-w-[80%]">
                  <RefreshCw size={14} className="animate-spin text-clever-teal" />
                  <span className="sr-only">{t('shared.neural_chat.thinking')}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="h-[64px] shrink-0 bg-white border-t border-gray-100 relative z-20">
          <form onSubmit={handleSend} className="w-full h-full flex items-center px-2">
            <div className="w-12 h-full flex items-center justify-center text-clever-teal shrink-0 pl-2">
              <Sparkles size={20} />
            </div>
            <div className="flex-1 h-full relative flex items-center px-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={placeholder}
                className="w-full bg-transparent outline-none text-gray-800 placeholder-gray-400 text-base font-medium h-full"
              />
              <div className={`absolute bottom-1 left-4 text-[9px] text-gray-300 uppercase tracking-widest font-bold transition-opacity duration-300 pointer-events-none ${isExpanded ? 'opacity-0' : 'opacity-100'}`}>
                {t('shared.neural_chat.enter_hint')}
              </div>
            </div>
            <div className="h-full flex items-center justify-center px-2">
              <button type="submit" className="w-10 h-10 rounded-full bg-gray-50 hover:bg-clever-teal hover:text-white flex items-center justify-center text-gray-400 transition-all duration-300">
                <ArrowRight size={18} />
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
